import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Popover } from 'antd';
import {
  FiFilter,
  FiDownload,
  FiCalendar,
  FiFileText,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiRefreshCw,
  FiUserPlus,
  FiRotateCcw,
  FiMoreVertical,
  FiExternalLink,
} from 'react-icons/fi';

import invoiceService from '@services/invoice';
import { toDateValue } from '@/utils/firestoreDate';
import { escapeCSVField, downloadCSV } from '@/utils/csvExport';
import {
  INVOICE_TYPE_FILTER_OPTIONS,
  INVOICE_STATUS_FILTER_OPTIONS,
  INVOICE_STATUS_LABEL,
  INVOICE_STATUS_TONE,
  getInvoiceTypeLabel,
  getInvoiceStatus,
  getInvoiceAmount,
  formatBillingPeriodLabel,
} from '@/utils/invoiceTaxonomy';

import { Text, textVariantsEnum, fontWeightEnum } from '@components/Text';
import { StatCard } from '@/components/StatCard';
import { StatusBadge } from '@/components/StatusBadge';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { DateRangePicker } from '@/components/DateRangePicker';
import { Button, buttonVariants } from '@/components/Button';
import { Table } from '@/components/Table';
import { Pagination } from '@/components/Pagination';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner.component';
import { ActionsMenu } from '@components/ActionsMenu';
import { addNotification, notificationStates } from '@components/Notification';

import { IS_DEMO_MODE } from '@/utils/demoMode';
import { DEMO_INVOICES } from './Invoices.demoData';

import styles from './Invoices.module.scss';

const PAGE_SIZE = 10;

const INVOICE_TYPE_ICON = {
  subscription: FiRefreshCw,
  upgrade: FiUserPlus,
  refund: FiRotateCcw,
  failed: FiXCircle,
};

function PlainCell({ children }) {
  return (
    <Text variant={textVariantsEnum.Text_sm} fontWeight={fontWeightEnum.Normal}>
      {children}
    </Text>
  );
}

function TypeCell({ type }) {
  const Icon = INVOICE_TYPE_ICON[type] || FiFileText;
  return (
    <div className={styles.typeCell}>
      <Icon size={13} color="var(--gray-500)" />
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Normal}
      >
        {getInvoiceTypeLabel(type)}
      </Text>
    </div>
  );
}

export const InvoicesComponent = () => {
  const { activeCompany } = useSelector((state) => state.auth.profile);

  const [invoicesList, setInvoicesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeMenuRow, setActiveMenuRow] = useState(null);
  const menuRef = useRef(null);

  const [dateRange, setDateRange] = useState(null);
  const [typeFilter, setTypeFilter] = useState(INVOICE_TYPE_FILTER_OPTIONS[0]);
  const [statusFilter, setStatusFilter] = useState(
    INVOICE_STATUS_FILTER_OPTIONS[0]
  );
  const [sortedBy, setSortedBy] = useState(null);
  const [reverseSort, setReverseSort] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchInvoices = () => {
    if (IS_DEMO_MODE) {
      setInvoicesList(DEMO_INVOICES);
      setLoading(false);
      return;
    }
    if (!activeCompany?.companyId) return;
    setLoading(true);
    invoiceService
      .fetchCompanyInvoices(activeCompany.companyId)
      .then((rows) => setInvoicesList(rows))
      .catch(() => setInvoicesList([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompany?.companyId]);

  const stats = useMemo(() => {
    let paidCount = 0;
    let paidAmount = 0;
    let pendingCount = 0;
    let pendingAmount = 0;
    let failedCount = 0;
    let failedAmount = 0;
    const currency = invoicesList[0]?.currency || 'inr';

    invoicesList.forEach((invoice) => {
      const status = getInvoiceStatus(invoice);
      const amount = getInvoiceAmount(invoice);
      if (status === 'paid') {
        paidCount += 1;
        paidAmount += amount;
      } else if (status === 'pending') {
        pendingCount += 1;
        pendingAmount += amount;
      } else if (status === 'failed') {
        failedCount += 1;
        failedAmount += amount;
      }
    });

    return {
      total: invoicesList.length,
      paidCount,
      paidAmount: invoiceService.formatCurrency(paidAmount, currency),
      pendingCount,
      pendingAmount: invoiceService.formatCurrency(pendingAmount, currency),
      failedCount,
      failedAmount: invoiceService.formatCurrency(failedAmount, currency),
      totalSpent: invoiceService.formatCurrency(paidAmount, currency),
    };
  }, [invoicesList]);

  const STAT_CARDS_CONFIG = [
    {
      key: 'total',
      label: 'Total Invoices',
      value: stats.total,
      caption: 'All time',
      icon: <FiFileText />,
      iconBg: 'var(--purple-100)',
    },
    {
      key: 'paid',
      label: 'Paid Invoices',
      value: stats.paidCount,
      caption: stats.paidAmount,
      icon: <FiCheckCircle />,
      iconBg: 'var(--success-100)',
    },
    {
      key: 'pending',
      label: 'Pending',
      value: stats.pendingCount,
      caption: stats.pendingAmount,
      icon: <FiClock />,
      iconBg: 'var(--warning-100)',
    },
    {
      key: 'failed',
      label: 'Failed',
      value: stats.failedCount,
      caption: stats.failedAmount,
      icon: <FiXCircle />,
      iconBg: 'var(--error-100)',
    },
    {
      key: 'totalSpent',
      label: 'Total Spent',
      value: stats.totalSpent,
      caption: 'All time',
      icon: <FiCalendar />,
      iconBg: 'var(--blue-100)',
    },
  ];

  const dateFilteredRows = useMemo(() => {
    const [start, end] = dateRange || [];
    if (!start || !end) return invoicesList;
    return invoicesList.filter((row) => {
      const d = toDateValue(row.createdAt) || toDateValue(row.payment?.paidAt);
      return Boolean(d) && d >= start && d <= end;
    });
  }, [invoicesList, dateRange]);

  const filteredRows = useMemo(() => {
    return dateFilteredRows.filter((row) => {
      if (typeFilter?.value && row.type !== typeFilter.value) return false;
      if (statusFilter?.value && getInvoiceStatus(row) !== statusFilter.value)
        return false;
      return true;
    });
  }, [dateFilteredRows, typeFilter, statusFilter]);

  const sortedRows = useMemo(() => {
    if (!sortedBy) return filteredRows;
    const getValue = (row) => {
      if (sortedBy === 'amount') return getInvoiceAmount(row);
      if (sortedBy === 'date')
        return toDateValue(row.payment?.paidAt)?.getTime() || 0;
      return 0;
    };
    const sorted = [...filteredRows].sort((a, b) => getValue(a) - getValue(b));
    return reverseSort ? sorted.reverse() : sorted;
  }, [filteredRows, sortedBy, reverseSort]);

  useEffect(() => {
    setCurrentPage(1);
  }, [dateRange, typeFilter, statusFilter, sortedBy, reverseSort]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / PAGE_SIZE));
  const pagedRows = sortedRows.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSortClick = (id, reverse) => {
    setSortedBy(id);
    setReverseSort(reverse);
  };

  const onActionsButtonClick = (e, rowData) => {
    setActiveMenuRow(rowData);
    menuRef.current.toggle(e);
  };

  const actionsMenuItems = useMemo(() => {
    if (!activeMenuRow) return [];
    return [
      {
        label: 'View Invoice',
        icon: <FiExternalLink size={16} />,
        onClick: () => {
          if (activeMenuRow.shortUrl) {
            window.open(
              activeMenuRow.shortUrl,
              '_blank',
              'noopener,noreferrer'
            );
          } else {
            addNotification({
              message: 'Invoice link not available',
              level: notificationStates.warning,
            });
          }
          menuRef.current?.hide();
        },
      },
    ];
  }, [activeMenuRow]);

  const handleDownloadInvoices = () => {
    if (sortedRows.length === 0) {
      addNotification({
        message: 'No invoices to download for the selected filters.',
        level: notificationStates.warning,
      });
      return;
    }
    const headers = [
      'Invoice #',
      'Type',
      'Date',
      'Amount',
      'Plan',
      'Status',
      'Billing Period',
    ];
    const rows = sortedRows.map((row) => {
      const paidAt = toDateValue(row.payment?.paidAt);
      return [
        escapeCSVField(row.invoiceNumber),
        escapeCSVField(getInvoiceTypeLabel(row.type)),
        escapeCSVField(paidAt ? paidAt.toDateString() : 'N/A'),
        escapeCSVField(
          invoiceService.formatCurrency(getInvoiceAmount(row), row.currency)
        ),
        escapeCSVField(row.planName),
        escapeCSVField(INVOICE_STATUS_LABEL[getInvoiceStatus(row)]),
        escapeCSVField(
          formatBillingPeriodLabel(row.billingPeriod, toDateValue)
        ),
      ].join(',');
    });
    const date = new Date().toISOString().split('T')[0];
    downloadCSV({ headers, rows, filename: `invoices-${date}.csv` });
    addNotification({
      message: 'Invoices exported successfully',
      level: notificationStates.success,
    });
  };

  const clearFilters = () => {
    setTypeFilter(INVOICE_TYPE_FILTER_OPTIONS[0]);
    setStatusFilter(INVOICE_STATUS_FILTER_OPTIONS[0]);
  };

  const filterPopoverContent = (
    <div className={styles.filterPopover}>
      <Dropdown
        label="Type"
        options={INVOICE_TYPE_FILTER_OPTIONS}
        value={typeFilter}
        onChange={setTypeFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Dropdown
        label="Status"
        options={INVOICE_STATUS_FILTER_OPTIONS}
        value={statusFilter}
        onChange={setStatusFilter}
        selectStyles={borderedSelectStyles}
        isSearchable={false}
      />
      <Button
        text="Clear filters"
        variant={buttonVariants.ghost}
        onClick={clearFilters}
      />
    </div>
  );

  const columns = [
    {
      id: 'invoiceNumber',
      name: 'Invoice #',
      style: { width: '14%' },
      cellRenderer: (row) => (
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Semibold}
        >
          {row.invoiceNumber}
        </Text>
      ),
    },
    {
      id: 'type',
      name: 'Type',
      style: { width: '17%' },
      cellRenderer: (row) => <TypeCell type={row.type} />,
    },
    {
      id: 'date',
      name: 'Date',
      sortable: true,
      sortKey: 'date',
      style: { width: '11%' },
      cellRenderer: (row) => {
        const paidAt = toDateValue(row.payment?.paidAt);
        return <PlainCell>{paidAt ? paidAt.toDateString() : 'N/A'}</PlainCell>;
      },
    },
    {
      id: 'amount',
      name: 'Amount',
      sortable: true,
      sortKey: 'amount',
      style: { width: '11%' },
      cellRenderer: (row) => (
        <Text
          variant={textVariantsEnum.Text_sm}
          fontWeight={fontWeightEnum.Semibold}
        >
          {invoiceService.formatCurrency(getInvoiceAmount(row), row.currency)}
        </Text>
      ),
    },
    {
      id: 'plan',
      name: 'Plan',
      style: { width: '11%' },
      cellRenderer: (row) => <PlainCell>{row.planName || '—'}</PlainCell>,
    },
    {
      id: 'status',
      name: 'Status',
      style: { width: '9%' },
      cellRenderer: (row) => {
        const status = getInvoiceStatus(row);
        return (
          <StatusBadge
            label={INVOICE_STATUS_LABEL[status]}
            tone={INVOICE_STATUS_TONE[status]}
          />
        );
      },
    },
    {
      id: 'billingPeriod',
      name: 'Billing Period',
      style: { width: '20%' },
      cellRenderer: (row) => (
        <PlainCell>
          {formatBillingPeriodLabel(row.billingPeriod, toDateValue)}
        </PlainCell>
      ),
    },
    {
      id: 'actions',
      name: 'Actions',
      style: { width: '7%' },
      cellRenderer: (row) => (
        <Button
          icon={<FiMoreVertical size={14} />}
          variant={buttonVariants.outline}
          ariaLabel="Actions"
          onClick={(e) => onActionsButtonClick(e, row)}
        />
      ),
    },
  ];

  return (
    <>
      <div className={styles.invoicesPage}>
        <div className={styles.invoicesContainer}>
          <div className={styles.invoicesHeader}>
            <div className={styles.invoicesHeaderTitle}>
              <Text
                variant={textVariantsEnum.Text_lg}
                fontWeight={fontWeightEnum.Semibold}
              >
                Invoices
              </Text>
              <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                View and manage all your billing history and payments.
              </Text>
            </div>
            <div className={styles.invoicesHeaderActions}>
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                allowClear
                className={styles.dateRangePicker}
              />
              <Popover
                content={filterPopoverContent}
                trigger="click"
                placement="bottomRight"
              >
                <Button
                  text="Filters"
                  icon={<FiFilter size={14} />}
                  variant={buttonVariants.outline}
                />
              </Popover>
              <Button
                text="Download Invoices"
                icon={<FiDownload size={14} />}
                variant={buttonVariants.primary}
                onClick={handleDownloadInvoices}
              />
            </div>
          </div>

          <div className={styles.statsRow}>
            {STAT_CARDS_CONFIG.map((card) => (
              <StatCard
                key={card.key}
                icon={card.icon}
                iconBg={card.iconBg}
                label={card.label}
                value={card.value}
                caption={card.caption}
              />
            ))}
          </div>

          <div className={styles.invoicesTableContainer}>
            {loading ? (
              <div className="w-100 text-center py-5">
                <Spinner />
              </div>
            ) : sortedRows.length === 0 ? (
              <EmptyState
                illustration
                title="No Invoices"
                description="No invoices match the selected filters."
              />
            ) : (
              <>
                <Table
                  className={styles.invoicesTable}
                  data={pagedRows}
                  columns={columns}
                  onSortClick={handleSortClick}
                  sortedBy={sortedBy}
                  reverseSort={reverseSort}
                />
                <div className={styles.tableFooter}>
                  <Text variant={textVariantsEnum.Text_sm} color="gray-500">
                    Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                    {Math.min(currentPage * PAGE_SIZE, sortedRows.length)} of{' '}
                    {sortedRows.length} invoices
                  </Text>
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ActionsMenu items={actionsMenuItems} ref={menuRef} />
    </>
  );
};

InvoicesComponent.displayName = 'Invoices';
